import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import LinkButton from 'assets/components/button/LinkButton';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import { addLog } from 'assets/components/feedback/Feedback';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import ThemeTagRounded from 'assets/components/tag/themes/Theme.Tag.Rounded';
import TextBox, { textBoxStyles } from 'assets/components/textBox/TextBox';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import Theme from 'assets/themes/Theme.Common';
import {
  date,
  defaultDateFormat,
  defaultDateTimeFormat,
  defaultServerDateTimeFormat,
  localizeDate,
} from 'assets/utils/data/Date';
import { autoTriggerDownload } from 'assets/utils/dom/Download';
import { createPathFromRoute, generateDataUrl } from 'assets/utils/dom/UrlParsing';
import MimeType from 'assets/utils/file/mimeType/MimeType';
import { HttpStatus } from 'config/Api.Config';
import { isEmpty } from 'lodash';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import { downloadLegacyPdfBase64 } from 'models/sales/contract/legacy/downloadLegacyPdf/Api.Contract.DownloadLegacyPdf';
import { SalesContractAction } from 'module/sales/views/contract/Sales.Contract.Hooks';
import { salesContractIndexRoute } from 'module/sales/views/contract/Sales.Contract.Index';
import { useMemo } from 'react';
import { useClaimEdit } from '../../ClaimEdit.Hooks';
import ClaimEditClientInfoForm from './ClaimEdit.ClientInfo.Form';
import ClaimEditContactInfoForm from './ClaimEdit.ContactInfo.Form';
import ClaimEditUpdate from './ClaimEdit.Update';
import ClaimEditContactInfo from './others/ClaimEdit.ContactInfo';

export const paragraphStyles: React.CSSProperties = {
  display: 'grid',
  flexWrap: 'wrap',
  gap: `${Theme.Size.M} ${Theme.Size.L}`,
  gridTemplateColumns: 'repeat(auto-fill, minmax(14em, 1fr))',
};
export default function ClaimEditInfo() {
  const { lang, translate } = getLocales();
  const { user, isDealer, isDistributor, isDealerOrDistributor } = useAuthStore();
  const {
    claim,
    contract,
    selectClaim,
    selectedClaim,
    reloadClaim,
    canEditClientInfo,
    canEditContactInfo,
    claimEditAction,
    setClaimEditAction,
    permissions,
    canEditClaim,
    product,
  } = useClaimEdit();

  const { payload: dealersPayload } = useApi({
    action: Dealer.list,
    body: {
      distributorId: user?.distributorId ? [user?.distributorId] : undefined,
    },
    wait: !isDealerOrDistributor,
  });
  const supportedDealers = useMemo(
    () => (!isDealer && isDistributor ? dealersPayload?.data : isDealer && isDistributor ? user?.dealers : []),
    [dealersPayload?.data, user?.dealers]
  );
  return !claim ? null : (
    <div id="info">
      {(permissions?.EDIT_CLAIM || permissions.CLAIM_ADMIN) && (
        <>
          <h2>{lang.info}</h2>
          <div style={paragraphStyles}>
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.number}: `}
              subtitle={claim?.number ?? 'N/A'}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.contact}: `}
              subtitle={claim.createdByPayee?.name || 'N/A'}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.client}: `}
              subtitle={contract?.client?.name ?? 'N/A'}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.incidentLocalDate}: `}
              subtitle={
                claim?.incidentLocalDate ? localizeDate(claim?.incidentLocalDate, defaultDateTimeFormat) : 'N/A'
              }
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.odometerReading}: `}
              subtitle={claim?.odometerReading ?? 'N/A'}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.workOrderNumber}: `}
              subtitle={claim?.workOrderNumber ?? 'N/A'}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.createdAt}: `}
              subtitle={date(claim?.createdAtUtc, defaultServerDateTimeFormat).format(defaultDateTimeFormat)}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.lastModified}: `}
              subtitle={date(claim?.modifiedAtUtc, defaultServerDateTimeFormat).format(defaultDateTimeFormat)}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.owner}: `}
              subtitle={claim?.owner?.fullName ?? 'N/A'}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: Theme.Size.S, marginTop: Theme.Size.L }}>
            <Button
              class={ThemeButtonCircle}
              disabled={canEditClaim}
              data={claim}
              onClick={(data) => {
                setClaimEditAction('updateClaim');
                selectClaim(data);
              }}
              media={<Icon class="fas-edit" />}
              style={{ display: 'inline-block' }}
            >
              {`${lang.edit} ${lang.claim}`}
            </Button>
            {claimEditAction === 'updateClaim' && claim.id === selectedClaim?.id && <ClaimEditUpdate />}
          </div>
          <br />
          <hr />
        </>
      )}
      {(permissions?.VIEW_CLAIM_CUSTOMERINFO || permissions?.EDIT_CLAIM_CUSTOMERINFO || permissions.CLAIM_ADMIN) && (
        <>
          <h2>{lang.clientInfo}</h2>
          <div style={paragraphStyles}>
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.firstName}: `}
              subtitle={contract?.client?.firstName ?? 'N/A'}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.lastName}: `}
              subtitle={contract?.client?.lastName ?? 'N/A'}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.phoneNumber}: `}
              subtitle={claim?.contactPhone ?? 'N/A'}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.email}: `}
              subtitle={claim?.contactEmail ?? 'N/A'}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.streetAddress}: `}
              subtitle={claim?.streetAddress ?? 'N/A'}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.muncipality}: `}
              subtitle={contract?.client?.muncipality ?? 'N/A'}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.postalCode}: `}
              subtitle={claim?.postalCode ?? 'N/A'}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.provinceCode}: `}
              subtitle={claim?.provinceCode ?? 'N/A'}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.countryCode}: `}
              subtitle={claim?.countryCode ?? 'N/A'}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.taxExempt}: `}
              subtitle={contract?.client?.taxExempt ? <Icon class="fas-check" /> : <Icon class="fas-times" />}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.legacy}: `}
              subtitle={contract?.client?.isLegacy ? <Icon class="fas-check" /> : <Icon class="fas-times" />}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: Theme.Size.S, marginTop: Theme.Size.L }}>
            <Button
              class={ThemeButtonCircle}
              disabled={canEditClientInfo}
              data={claim}
              onClick={(data) => {
                setClaimEditAction('editClientInfo');
                selectClaim(data);
              }}
              media={<Icon class="fas-edit" />}
              style={{ display: 'inline-block' }}
            >
              {`${lang.edit} ${lang.clientInfo} `}
            </Button>
            {claimEditAction === 'editClientInfo' && claim.id === selectedClaim?.id && (
              <ClaimEditClientInfoForm onSave={reloadClaim} onCancel={() => selectClaim(undefined)} data={claim} />
            )}
          </div>
          <br />
          <hr />
        </>
      )}
      {(permissions?.EDIT_CLAIMPAYEE || permissions?.VIEW_CLAIMPAYEE || permissions.CLAIM_ADMIN) && (
        <>
          <ClaimEditContactInfo claim={claim} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: Theme.Size.S, marginTop: Theme.Size.L }}>
            <Button
              class={ThemeButtonCircle}
              disabled={canEditContactInfo}
              data={claim}
              onClick={(data) => {
                selectClaim(data);
                setClaimEditAction('editContactInfo');
              }}
              media={<Icon class="fas-edit" />}
              style={{ display: 'inline-block' }}
            >
              {`${lang.edit} ${lang.contactInfo}`}
            </Button>
            {claimEditAction === 'editContactInfo' && claim.id === selectedClaim?.id && (
              <ClaimEditContactInfoForm onSave={reloadClaim} onCancel={() => selectClaim(undefined)} data={claim} />
            )}
          </div>
          <br />
          <hr />
        </>
      )}
      {(permissions?.VIEW_CLAIM_VEHICLEINFO || permissions.CLAIM_ADMIN) && (
        <>
          <h2>{lang.vehicleInfo}</h2>
          <div style={paragraphStyles}>
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.year}: `}
              subtitle={contract?.vehicle?.year}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.make}: `}
              subtitle={contract?.vehicle?.makeCode}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.model}: `}
              subtitle={contract?.vehicle?.model}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.trimLevel}: `}
              subtitle={contract?.vehicle?.trimLevel}
            />
            <TextBox class={textBoxStyles.block.flexBlock} title={`${lang.vin}: `} subtitle={contract?.vehicle?.vin} />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.bodyType}: `}
              subtitle={contract?.vehicle?.bodyType}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.engineInfo}: `}
              subtitle={contract?.vehicle?.engineInfo}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.engineType}: `}
              subtitle={translate(contract?.vehicle?.engineType)}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.purchasePrice}: `}
              subtitle={
                contract?.vehicle?.purchasePrice ? (
                  <FormatValue value={`$${contract?.vehicle?.purchasePrice?.toFixed(2)}`} />
                ) : (
                  'N/A'
                )
              }
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.deliveryDate}: `}
              subtitle={
                (contract?.vehicle?.deliveryDate && localizeDate(contract?.vehicle?.deliveryDate, defaultDateFormat)) ??
                'N/A'
              }
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.inServiceDate}: `}
              subtitle={
                (contract?.vehicle?.inServiceDate &&
                  localizeDate(contract?.vehicle?.inServiceDate, defaultDateFormat)) ??
                'N/A'
              }
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.purchaseState}: `}
              subtitle={contract?.vehicle?.purchaseState}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.legacy}: `}
              subtitle={contract?.vehicle?.isLegacy ? <Icon class="fas-check" /> : <Icon class="fas-times" />}
            />
          </div>
          <br />
          <hr />
        </>
      )}
      {(permissions?.VIEW_CLAIM_CONTRACTINFO || permissions.CLAIM_ADMIN) && (
        <>
          <h2>{lang.contractInfo}</h2>
          <div style={paragraphStyles}>
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.appNumber}:`}
              subtitle={contract?.appNumberWithRevision}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.contract} ${lang.status}: `}
              subtitle={
                <Box
                  class={ThemeTagRounded}
                  style={{
                    backgroundColor: contract?.statusCode === 'PAID_ACTIVE' ? Theme.Color.success : Theme.Color.error,
                    fontSize: Theme.FontSize.S,
                  }}
                >
                  {lang[contract?.statusCode]?.replace(/_/g, ' - ') ?? 'N/A'}
                </Box>
              }
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.effectiveDate}: `}
              subtitle={(contract?.effectiveDate && localizeDate(contract?.effectiveDate, defaultDateFormat)) ?? 'N/A'}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.expiryDate}: `}
              subtitle={(contract?.expiryDate && localizeDate(contract?.expiryDate, defaultDateFormat)) ?? 'N/A'}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.sellingDealership}: `}
              subtitle={contract?.dealer?.name ?? 'N/A'}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.termInMonths}: `}
              subtitle={contract?.termInMonths ?? 'N/A'}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.deductible}: `}
              subtitle={contract?.deductible ?? 'N/A'}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.product}: `}
              subtitle={contract?.legacyProductName ?? product?.name ?? lang.unknownProduct}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.surcharges}:`}
              subtitle={!isEmpty(contract?.subfees) ? contract?.subfees?.map((it) => it.name)?.join(', ') : 'N/A'}
            />
            <TextBox
              class={textBoxStyles.block.flexBlock}
              title={`${lang.enhancements}: `}
              subtitle={
                !isEmpty(contract?.contractEnhancements)
                  ? contract?.contractEnhancements?.map((it) => it.productEnhancement?.name)?.join(', ')
                  : 'N/A'
              }
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: Theme.Size.S, marginTop: Theme.Size.L }}>
            {contract?.legacyId ? (
              <Button
                class={ThemeButtonCircle}
                disabled={!contract?.id}
                onClick={async () => {
                  const res = await downloadLegacyPdfBase64({ legacyId: contract?.legacyId });

                  if (res?.status === HttpStatus.ok) {
                    autoTriggerDownload({
                      url: generateDataUrl(MimeType.appPDF, res?.payload),
                      name: `Contract_${contract?.appNumber}.pdf`,
                    });
                    addLog({ success: lang.downloadWasSuccessful });
                  } else addLog({ error: res.message });
                }}
                media={<Icon class="fas-external-link-square-alt" />}
                style={{ display: 'inline-block' }}
              >
                {lang.getLegacyContractDocument}
              </Button>
            ) : (
              <>
                <LinkButton
                  class={ThemeButtonCircle}
                  url={contract?.documentationAggregate?.url}
                  disabled={!contract?.documentationAggregate?.url}
                  media={<Icon class="fas-download" />}
                  target="_blank"
                >
                  {`${lang.download} ${lang.contractDocument} ${contract?.appNumber}`}
                </LinkButton>
                <Button
                  class={ThemeButtonCircle}
                  disabled={
                    !contract?.id ||
                    (isDealerOrDistributor && !supportedDealers?.some((it) => it.id === contract?.dealerId))
                  }
                  onClick={(data) => {
                    window.open(
                      createPathFromRoute(salesContractIndexRoute, {
                        ...data,
                      }),
                      '_blank'
                    );
                  }}
                  htmlElementProps={{
                    title:
                      isDealerOrDistributor && !supportedDealers?.some((it) => it.id === contract?.dealerId)
                        ? lang.unauthorized
                        : undefined,
                  }}
                  data={{ id: contract?.id?.toString(), action: SalesContractAction.INFO }}
                  media={<Icon class="fas-external-link-square-alt" />}
                  style={{ display: 'inline-block' }}
                >
                  {`${lang.view} ${lang.contract}`}
                </Button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
